import FormInput from 'components/FormInput';
import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import TypeAheadInput from 'components/TypeAheadInput';
import { MoreIcon, Icon } from 'utils/icons';
import DropDownMenu from 'components/DropDownMenu';
import { calculateItemTotal, calculateOvertimeUnitRate } from 'utils/common';
import { fixNumber, formatCurrency, getCurrencySymbol } from 'utils/helper';
import { normalizeCurrencyCode } from 'utils/currencies';
import pluralize from 'pluralize';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import MultiSelectDropdown from 'components/MultiSelectDropdown';
import { useFringeQuery, useGroupFringeQuery } from 'utils/api/queries';
import {
  evaluateFormulaDisplayValue,
  expressionFieldFor,
  formatDecimalDisplayValue,
  hasFormulaMathOperator,
  isFormulaExpressionValue,
  isIncompleteFormulaExpressionValue,
  isIncompleteDecimalValue,
  normalizeFormulaPayloadValue,
  normalizeDecimalPayloadValue,
  SKIP_DECIMAL_FIELD,
} from '../utils/rowPayload';
import FormulaBar from '../FormulaBar';
import TagCellPicker from '../TagCellPicker';
import API_ENDPOINTS from 'utils/api/endpoints';
import {
  buildAiAutocompleteScopeKey,
  getAiAutocompleteSuggestions,
  getPreviousAiItemLabels,
  storeAiAutocompleteSuggestions,
} from './aiAutocompleteCache';

const getStableRowKey = row => {
  const rowKey = row?.id ?? row?.row_id;
  return rowKey == null ? '' : String(rowKey);
};

const isPlaceholderRow = row => {
  const rowKey = getStableRowKey(row);
  return (
    Boolean(row?.is_placeholder) ||
    Boolean(row?._isDraft) ||
    rowKey.startsWith('draft-') ||
    rowKey.startsWith('temp-')
  );
};

const normalizeRequiredDecimalValue = (value, fallback = '1') => {
  if (isFormulaExpressionValue(value)) return fallback;
  const fixed = fixNumber(value, false);
  if (fixed === null || fixed === undefined || fixed === '') return fallback;
  if (isIncompleteDecimalValue(fixed, { allowNegative: false })) {
    return fallback;
  }
  return fixed;
};

const isFormulaEditableColumn = column =>
  column?.dataType === 'decimal' &&
  column?.type !== 'showValue' &&
  ['quantity', 'rate', 'multiplier'].includes(column.key);

const GLOBAL_UNIT_FIELD_BY_COLUMN = {
  quantity: 'unit',
  multiplier: 'multiplier_unit',
};

const ItemRow = ({
  columns,
  rowData,
  allRows,
  addRow,
  onItemFieldCommit,
  hiddenColumn,
  onKeyUp,
  onKeyDown,
  lastIndex,
  lastObjectId,
  index,
  canEdit,
  handleRowAction,
  addEmptyRow,
  hideOptions,
  focusEl,
  setFocusEl,
  focus,
  fringeRowCount,
  isChecked,
  onCheckboxChange,
  setShowCheckboxColumn,
  setShowModal,
  setSelectedFringe,
  fringeRows,
  selectedFringe,
  hoverData,
  highlightedRows,
  fringeGroups,
  hoveredFringeId,
  setHoveredFringeId,
  hoveredRowId,
  setHoveredRowId,
  refreshLinkedFringes,
  detailSheetType,
  cellPresenceMap,
  onCellPresenceFocus,
  onCellPresenceBlur,
  onDetailCellFocus,
  onClipboardCellFocus,
  multiCellSelection,
  budgetGlobals,
  budgetTags,
  refetchBudgetTags,
  columnDragState,
  selectedItem,
  commentCounts,
  onCommentCellFocus,
  onCellCommentContextMenu,
  onOpenCellComments,
}) => {
  const selectedRowKeys = new Set(
    (multiCellSelection?.selection?.rowKeys || []).map(String)
  );
  const selectedMenuRows = (allRows || []).filter(row =>
    selectedRowKeys.has(getStableRowKey(row))
  );
  const appliesToMultipleRows =
    selectedMenuRows.length > 1 &&
    selectedRowKeys.has(getStableRowKey(rowData));

  const lineItemOptions = [
    {
      value: 'insert-above-item',
      label: `Insert New Row${appliesToMultipleRows ? 's' : ''} Above`,
      icon: (
        <Icon
          name="CaretUpIcon"
          style={{ marginRight: '8px', width: '14px' }}
        />
      ),
    },
    {
      value: 'insert-below-item',
      label: `Insert New Row${appliesToMultipleRows ? 's' : ''} Below`,
      icon: (
        <Icon
          name="CaretDownIcon"
          style={{ marginRight: '8px', width: '14px' }}
        />
      ),
    },
    { value: 'divider' },
    {
      value: 'move-item',
      label: `Move Line Item${appliesToMultipleRows ? 's' : ''}…`,
      icon: (
        <Icon
          name="ArrowUpFromBracketIcon"
          style={{ marginRight: '8px', width: '14px' }}
        />
      ),
    },
    {
      value: 'duplicate-item',
      label: `Duplicate Line Item${appliesToMultipleRows ? 's' : ''}`,
      icon: (
        <Icon name="CloneIcon" style={{ marginRight: '8px', width: '14px' }} />
      ),
    },
    {
      value: 'delete-item',
      label: `Delete Line Item${appliesToMultipleRows ? 's' : ''}`,
      icon: (
        <Icon
          name="TrashCanIcon"
          style={{ marginRight: '8px', width: '14px' }}
        />
      ),
    },
  ];

  const { selectedBudget, activeBudget } = useHomeScreenContext();
  const primaryCurrency = normalizeCurrencyCode(
    selectedBudget?.primary_currency
  );
  const [, setFringeTotal] = useState(0);
  const [selectedFruits, setSelectedFruits] = useState([]);
  const { data: fringeIdData } = useFringeQuery.useFringeDetailsQuery({
    filters: { instance_type: 'budget', active_id: activeBudget },
  });
  const fringeData = fringeIdData?.data;

  const { data: groupFringeQuery } =
    useGroupFringeQuery.useGroupFringeDetailsQuery({
      filters: { instance_type: 'budget', active_id: activeBudget },
      enabled: true,
    });
  const groupFringeData = groupFringeQuery?.data?.data;

  const groupOptions = useMemo(() => {
    if (!groupFringeData) return [];

    // Use API identifier format so it round-trips cleanly with backend
    return groupFringeData.map(group => ({
      id: `group-${group.id}`,
      value: group.name,
      label: group.name,
      type: 'group',
      display_name: group.name,
      color: group.color,
    }));
  }, [groupFringeData]);

  const fringeOptions = useMemo(() => {
    if (!fringeData?.data) return [];

    // Use API identifier format so it round-trips cleanly with backend
    return fringeData.data.map(fringe => ({
      id: `fringe-${fringe.id}`,
      value: fringe.abbreviation,
      label: fringe.abbreviation,
      type: 'fringe',
      display_name: fringe.display_name,
      color: fringe.color,
    }));
  }, [fringeData]);
  const tagOptions = useMemo(
    () =>
      (budgetTags || []).map(tag => ({
        id: tag.id,
        value: tag.name,
        label: tag.name,
        name: tag.name,
        color: tag.color,
      })),
    [budgetTags]
  );
  //|| '#3632a8'
  const handleFruitChange = updatedSelection => {
    const updatedWithColor = updatedSelection.map(selection => {
      const fringe = fringeOptions.find(f => f.id === selection.id);
      return {
        ...selection,
        color: fringe?.color, // Default color if not found
      };
    });
    // // 1) Store in local state if you want to keep it tracked:
    setSelectedFruits(updatedWithColor);

    // 2) Merge into the newRow so it gets sent in payload:
    const itemRow = {
      ...rowData,
      ...newRow,
      fringe: updatedWithColor, // convert to int
    };

    // 3) Recalculate item_total if necessary, or omit if not needed
    itemRow.item_total = calculateItemTotal(itemRow, selectedBudget);
    itemRow.column_total =
      parseFloat(itemRow.item_total || 0) +
      parseFloat(itemRow.fringe_total || 0);

    // 4) Update local newRow state
    setNewRow(itemRow);

    // 5) Let the existing logic handle uploading:
    const dataToAdd = processDataToAdd(itemRow);
    const hasDataToPost =
      Object.hasOwn(dataToAdd, 'fringe') ||
      columns?.some(col => Object.hasOwn(dataToAdd, col.key));

    if (
      addNewRow &&
      (isPlaceholderRow(newRow) || isPlaceholderRow(rowData)) &&
      hasDataToPost
    ) {
      addEmptyRow(newRow?.row_id ?? newRow?.id);
      setAddNewRow(false);
    }

    if (hasDataToPost) {
      pendingDataRef.current = null;
      addRow(dataToAdd);
    }
  };

  const handleTagChange = updatedSelection => {
    const itemRow = {
      ...rowData,
      ...newRow,
      tags: updatedSelection,
    };

    setNewRow(itemRow);

    const dataToAdd = processDataToAdd(itemRow);
    const hasDataToPost =
      Object.hasOwn(dataToAdd, 'tags') ||
      columns?.some(col => Object.hasOwn(dataToAdd, col.key));

    if (
      addNewRow &&
      (isPlaceholderRow(newRow) || isPlaceholderRow(rowData)) &&
      hasDataToPost
    ) {
      addEmptyRow(newRow?.row_id ?? newRow?.id);
      setAddNewRow(false);
    }

    if (hasDataToPost) {
      pendingDataRef.current = null;
      addRow(dataToAdd);
    }
  };

  const { setTabActive } = useHomeScreenContext();

  const hasData = useCallback(
    (row, fieldKey) => {
      const field = columns.find(column => column.key === fieldKey);
      if (!field) return true;

      const hasVal =
        field.dataType === 'decimal'
          ? !Number.isNaN(fixNumber(fixNumber(row[field.key]))) &&
            parseFloat(fixNumber(row[field.key])) >= 0
          : row[field.key];
      return Boolean(row.id || hasVal);
    },
    [columns]
  );

  const [formControlValue] = useState('');
  const [addNewRow, setAddNewRow] = useState(true);
  const [newRow, setNewRow] = useState({});
  const [unitSuggestions, setUnitSuggestions] = useState([]);
  const hasTriedToAddRowRef = useRef(false); // Use ref for immediate updates without re-renders
  const pendingDataRef = useRef(null);
  const focusCountRef = useRef(0);
  const isRowFocusedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const addRowRef = useRef(addRow);
  const expressionDisplayBackupRef = useRef({});
  const lastServerRowRef = useRef(null);
  const globalSuggestionHideTimerRef = useRef(null);
  const globalSuggestionHandledKeyRef = useRef(null);
  const [globalSuggestionState, setGlobalSuggestionState] = useState(null);
  const [activeFormulaColumnKey, setActiveFormulaColumnKey] = useState(null);
  const [aiItemSuggestions, setAiItemSuggestions] = useState([]);
  const [aiItemSuggestionIndex, setAiItemSuggestionIndex] = useState(0);
  const aiItemTimerRef = useRef(null);
  const aiItemRequestIdRef = useRef(0);
  const aiItemAbortControllerRef = useRef(null);
  const aiItemSuppressKeyUpRef = useRef(null);
  const itemAutocompleteRef = useRef(null);
  const aiItemMenuRef = useRef(null);
  const itemEditStartValueRef = useRef(null);
  const [aiItemMenuBounds, setAiItemMenuBounds] = useState(null);
  const aiItemPreviousItems = useMemo(
    () =>
      getPreviousAiItemLabels({
        rows: allRows,
        currentRow: rowData,
        rowIndex: index,
      }),
    [allRows, index, rowData]
  );
  const aiAutocompleteScopeKey = useMemo(
    () =>
      buildAiAutocompleteScopeKey({
        budgetId: selectedBudget?.budget,
        categoryId: selectedItem?.id,
        previousItems: aiItemPreviousItems,
      }),
    [aiItemPreviousItems, selectedBudget?.budget, selectedItem?.id]
  );

  const dismissAiItemSuggestions = useCallback(() => {
    aiItemRequestIdRef.current += 1;
    if (aiItemTimerRef.current) clearTimeout(aiItemTimerRef.current);
    aiItemAbortControllerRef.current?.abort();
    aiItemAbortControllerRef.current = null;
    setAiItemSuggestions([]);
    setAiItemSuggestionIndex(0);
  }, []);

  const getRowCurrency = useCallback(
    row =>
      normalizeCurrencyCode(
        row?.currency ?? newRow?.currency ?? rowData?.currency,
        primaryCurrency
      ),
    [newRow?.currency, primaryCurrency, rowData?.currency]
  );

  const normalizedBudgetGlobals = useMemo(
    () =>
      (budgetGlobals || [])
        .map(globalItem => ({
          ...globalItem,
          abbreviation: String(globalItem?.abbreviation || '').toUpperCase(),
          name: globalItem?.name || '',
          displayValue: formatDecimalDisplayValue(globalItem?.value),
        }))
        .filter(globalItem => Boolean(globalItem.abbreviation)),
    [budgetGlobals]
  );

  const getGlobalUnitForCellValue = useCallback(
    (field, value, selectedGlobal) => {
      if (!GLOBAL_UNIT_FIELD_BY_COLUMN[field?.key]) return '';

      const selectedGlobalUnit = String(selectedGlobal?.units || '').trim();
      if (selectedGlobalUnit) return selectedGlobalUnit;

      const normalizedValue = normalizeFormulaPayloadValue(value);
      const matchedGlobal = normalizedBudgetGlobals.find(
        globalItem => globalItem.abbreviation === normalizedValue
      );

      return String(matchedGlobal?.units || '').trim();
    },
    [normalizedBudgetGlobals]
  );

  const getFormulaDisplayValue = useCallback((column, value, sourceRow) => {
    if (
      isFormulaEditableColumn(column) &&
      sourceRow?.[expressionFieldFor(column.key)]
    ) {
      return formatDecimalDisplayValue(value);
    }

    return column?.formatValue && column.key !== 'item_total'
      ? column?.formatValue(value)
      : value;
  }, []);

  const getFormulaTokenContext = (value, cursorIndex) => {
    const text = String(value ?? '');
    const activeElement =
      typeof document === 'undefined' ? null : document.activeElement;
    const cursor =
      typeof cursorIndex === 'number'
        ? cursorIndex
        : typeof activeElement?.selectionStart === 'number'
        ? activeElement.selectionStart
        : text.length;
    const beforeCursor = text.slice(0, cursor);
    const tokenMatch = beforeCursor.match(/([A-Za-z_][A-Za-z0-9_]*)$/);

    if (!tokenMatch) return null;

    const query = tokenMatch[1].toUpperCase();
    return {
      query,
      tokenStart: cursor - tokenMatch[1].length,
      tokenEnd: cursor,
    };
  };

  const updateGlobalSuggestions = useCallback(
    (field, value, inputName, cursorIndex) => {
      if (!isFormulaEditableColumn(field)) {
        setGlobalSuggestionState(null);
        return;
      }

      const tokenContext = getFormulaTokenContext(value, cursorIndex);
      if (!tokenContext?.query || !normalizedBudgetGlobals.length) {
        setGlobalSuggestionState(null);
        return;
      }

      const suggestions = normalizedBudgetGlobals
        .filter(globalItem => {
          const name = globalItem.name.toUpperCase();
          return (
            globalItem.abbreviation.startsWith(tokenContext.query) ||
            name.includes(tokenContext.query)
          );
        })
        .slice(0, 6);

      if (!suggestions.length) {
        setGlobalSuggestionState(null);
        return;
      }

      setGlobalSuggestionState(previousState => {
        const isSameSuggestionContext =
          previousState?.columnKey === field.key &&
          previousState?.inputName === inputName &&
          previousState?.query === tokenContext.query;
        const activeIndex = isSameSuggestionContext
          ? Math.min(previousState?.activeIndex ?? 0, suggestions.length - 1)
          : 0;

        return {
          columnKey: field.key,
          inputName,
          ...tokenContext,
          activeIndex,
          suggestions,
        };
      });
    },
    [normalizedBudgetGlobals]
  );

  const hasUnresolvedFormulaGlobals = useCallback(
    value => {
      if (!normalizedBudgetGlobals.length) return false;

      const knownGlobals = new Set(
        normalizedBudgetGlobals.map(globalItem => globalItem.abbreviation)
      );
      const identifiers =
        normalizeFormulaPayloadValue(value).match(
          /\b[A-Za-z_][A-Za-z0-9_]*\b/g
        ) || [];

      return identifiers.some(identifier => !knownGlobals.has(identifier));
    },
    [normalizedBudgetGlobals]
  );

  const hideGlobalSuggestions = useCallback(() => {
    if (globalSuggestionHideTimerRef.current) {
      clearTimeout(globalSuggestionHideTimerRef.current);
    }
    globalSuggestionHideTimerRef.current = setTimeout(() => {
      setGlobalSuggestionState(null);
    }, 120);
  }, []);

  const processDataToAdd = itemRow =>
    Object.keys(itemRow)?.reduce((acc, fieldKey) => {
      if (
        fieldKey === 'currency' &&
        columns.some(col => col.key === 'currency')
      ) {
        return {
          ...acc,
          currency: normalizeCurrencyCode(itemRow.currency, primaryCurrency),
        };
      }

      if (
        fieldKey === 'account_number' &&
        columns.some(col => col.key === 'account_number')
      ) {
        return {
          ...acc,
          account_number: (itemRow.account_number ?? '')
            .toString()
            .slice(0, 14),
        };
      }

      if (!hasData(itemRow, fieldKey)) return acc;

      const colInfo = columns.find(column => column.key === fieldKey);
      if (colInfo?.dataType === 'decimal') {
        if (
          isFormulaEditableColumn(colInfo) &&
          isFormulaExpressionValue(itemRow[fieldKey])
        ) {
          if (isIncompleteFormulaExpressionValue(itemRow[fieldKey])) {
            return acc;
          }

          return {
            ...acc,
            [expressionFieldFor(fieldKey)]: normalizeFormulaPayloadValue(
              itemRow[fieldKey]
            ),
          };
        }

        const decimalValue = normalizeDecimalPayloadValue(
          itemRow[fieldKey],
          colInfo
        );
        if (decimalValue === SKIP_DECIMAL_FIELD) return acc;
        return {
          ...acc,
          [fieldKey]: decimalValue,
          ...(isFormulaEditableColumn(colInfo)
            ? { [expressionFieldFor(fieldKey)]: '' }
            : {}),
        };
      }

      return { ...acc, [fieldKey]: itemRow[fieldKey] };
    }, {});

  useEffect(() => {
    const previousServerRow = lastServerRowRef.current;
    const changedServerFormulaFields = new Set(
      columns
        .filter(isFormulaEditableColumn)
        .map(column => expressionFieldFor(column.key))
        .filter(
          field =>
            Object.hasOwn(rowData, field) &&
            (rowData[field] ?? '') !== (previousServerRow?.[field] ?? '')
        )
    );
    lastServerRowRef.current = rowData;
    const activeCellName = document.activeElement?.getAttribute?.('name');
    const activeCellIndex = Number.parseInt(
      activeCellName?.split?.(',')?.[1],
      10
    );
    const isActiveInputInThisRow = activeCellIndex === index;

    setNewRow(prev => {
      const rowIdentifier = prev?.id ?? rowData?.id;
      const isServerBackedRow =
        Boolean(rowIdentifier) && !String(rowIdentifier).startsWith('temp-');

      if (isServerBackedRow) {
        hasTriedToAddRowRef.current = false;

        // Formula-only updates (such as freezing a deleted global) must refresh
        // even when an older server response retains the previous timestamp.
        // Compare server snapshots, not the user's in-progress local formula.
        if (
          (rowData?.updated_on && rowData.updated_on !== prev.updated_on) ||
          changedServerFormulaFields.size > 0
        ) {
          const synced = {
            ...prev,
            id: rowData.id ?? prev.id,
            row_id: prev.row_id ?? rowData.row_id,
            row_order: rowData.row_order ?? prev.row_order,
            position: rowData.position ?? prev.position,
            is_placeholder: rowData.is_placeholder ?? prev.is_placeholder,
            _isDraft: rowData._isDraft ?? prev._isDraft,
            updated_on: rowData.updated_on ?? prev.updated_on,
          };
          columns.forEach(col => {
            const expressionField = expressionFieldFor(col.key);
            const refreshUneditedFormula =
              changedServerFormulaFields.has(expressionField) &&
              (prev[expressionField] ?? '') ===
                (previousServerRow?.[expressionField] ?? '');
            const shouldPreserveFocusedInputValue =
              (isRowFocusedRef.current || isActiveInputInThisRow) &&
              !refreshUneditedFormula &&
              col.key !== 'item_total' &&
              col.key !== 'fringe_total' &&
              col.key !== 'column_total' &&
              (col.type === 'text' ||
                col.type === 'select' ||
                col.dataType === 'decimal');

            if (hasData(rowData, col.key) && !shouldPreserveFocusedInputValue) {
              synced[col.key] =
                refreshUneditedFormula &&
                isActiveInputInThisRow &&
                activeCellName?.split?.(',')?.[0] === col.key
                  ? rowData[expressionField] || rowData[col.key]
                  : getFormulaDisplayValue(col, rowData[col.key], rowData);
            }
            if (isFormulaEditableColumn(col)) {
              synced[expressionField] =
                rowData?.[expressionField] ?? prev?.[expressionField] ?? '';
            }
          });
          if (
            !(
              isActiveInputInThisRow &&
              activeCellName?.split?.(',')?.[0] === 'account_number'
            )
          ) {
            synced.account_number =
              rowData?.account_number ?? prev.account_number ?? '';
          }
          synced.fringe_total = rowData?.fringe_total ?? prev.fringe_total;
          synced.column_total = rowData?.column_total ?? prev.column_total;
          synced.item_total = rowData?.item_total ?? prev.item_total;
          return synced;
        }

        return prev;
      }

      // New row — initial sync from rowData
      return {
        ...(!prev.row_id ? rowData : { ...prev, id: rowData?.id }),
        ...(prev.row_id
          ? null
          : columns.reduce((acc, col) => {
              if (hasData(rowData, col.key)) {
                acc[col.key] = getFormulaDisplayValue(
                  col,
                  rowData[col.key],
                  rowData
                );
              } else {
                acc[col.key] =
                  col.defaultValue !== undefined
                    ? col.defaultValue
                    : col.dataType === 'decimal'
                    ? '0.0000'
                    : '';
              }
              if (isFormulaEditableColumn(col)) {
                acc[expressionFieldFor(col.key)] =
                  rowData?.[expressionFieldFor(col.key)] || '';
              }
              return acc;
            }, {})),
      };
    });

    let focusTimeoutId;
    if (focusEl) {
      focusTimeoutId = setTimeout(() => {
        const element = document.getElementsByName(focusEl)?.[0];
        element?.focus();
        element?.select();
        element?.setSelectionRange(0, element.value.length);
        setFocusEl(null);
      }, 200);
    }

    return () => {
      if (focusTimeoutId) clearTimeout(focusTimeoutId);
    };
  }, [
    columns,
    focusEl,
    getFormulaDisplayValue,
    hasData,
    index,
    rowData,
    setFocusEl,
  ]);

  useEffect(() => {
    if (!rowData?._multiEditRevision || !rowData?._multiEditField) return;

    const column = columns.find(col => col.key === rowData._multiEditField);
    if (!column) return;

    setNewRow(prev => {
      const nextValue = getFormulaDisplayValue(
        column,
        rowData[column.key],
        rowData
      );
      const nextFringeTotal = rowData.fringe_total ?? prev.fringe_total;

      if (
        prev[column.key] === nextValue &&
        prev.item_total === rowData.item_total &&
        prev.fringe_total === nextFringeTotal &&
        prev.column_total === rowData.column_total
      ) {
        return prev;
      }

      return {
        ...prev,
        [column.key]: nextValue,
        ...(isFormulaEditableColumn(column)
          ? {
              [expressionFieldFor(column.key)]:
                rowData?.[expressionFieldFor(column.key)] ||
                prev?.[expressionFieldFor(column.key)] ||
                '',
            }
          : {}),
        item_total: rowData.item_total,
        fringe_total: nextFringeTotal,
        column_total: rowData.column_total,
      };
    });
  }, [columns, getFormulaDisplayValue, rowData]);

  useEffect(() => {
    if (rowData?.fringe) {
      // Normalize persisted fringe/group values coming from backend
      // so they match the option shape expected by MultiSelectDropdown.
      const allOptions = [...groupOptions, ...fringeOptions];

      const normalizedSelection = rowData.fringe
        .map(entry => {
          // Already in the correct shape
          if (entry && typeof entry === 'object' && entry.id) {
            return entry;
          }

          // Try to map primitive/legacy values (e.g. IDs) back to option objects
          const id = String(entry?.id ?? entry);
          return allOptions.find(opt => opt.id === id) || null;
        })
        .filter(Boolean);

      setSelectedFruits(normalizedSelection);
    } else {
      setSelectedFruits([]);
    }
  }, [rowData, groupOptions, fringeOptions]);

  const fixedDecimalRate = value => {
    if (value !== null && value !== undefined && value !== '') {
      const isNegative = /^-/.test(value);
      let filteredInput = value.toString().replace(/[^\d.]/g, '');
      const parts = filteredInput.split('.');
      // Remove leading zeros from the integer part
      // parts[0] = parts[0].replace(/^0+/, '') || '0';
      // If there's a decimal part, truncate it to 4 digits
      if (parts.length > 1) {
        parts[1] = parts[1].substring(0, 4);
        filteredInput = parts[0] + '.' + parts[1];
      } else {
        filteredInput = parts[0];
      }
      return (isNegative ? '-' : '') + filteredInput;
    }
    return value;
  };

  const formatRateWithCommas = value => {
    if (!value && value !== 0) return value;

    const isNegative = /^-/.test(String(value));
    const filteredInput = value.toString().replace(/[^\d.]/g, '');
    const [integer = '0', decimal = ''] = filteredInput.split('.');
    const formattedInteger = integer.replace(/^0+/, '') || '0';
    const formattedWithCommas = formattedInteger.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ','
    );
    const trimmedDecimal =
      decimal?.length > 0 ? decimal.substring(0, 4) : undefined;

    return (
      (isNegative ? '-' : '') +
      (trimmedDecimal !== undefined && trimmedDecimal !== ''
        ? `${formattedWithCommas}.${trimmedDecimal}`
        : formattedWithCommas)
    );
  };

  const handleNewRowChange = field => event => {
    if (!canEdit) return false;
    // setKeyStore(field.key);
    const eventValue = event?.target?.value;
    let value = eventValue !== undefined ? eventValue : event;
    if (field?.key === 'currency') {
      value = normalizeCurrencyCode(value, primaryCurrency);
    }
    const isFormulaInput =
      isFormulaEditableColumn(field) && isFormulaExpressionValue(value);
    value =
      field?.dataType === 'decimal' && !isFormulaInput
        ? fixedDecimalRate(value)
        : value;
    if (field?.maxLength && typeof value === 'string') {
      value = value.slice(0, field.maxLength);
    }
    const isFormulaBarInput = Boolean(event?.target?.dataset?.formulaBarInput);
    if (isFormulaBarInput) {
      setGlobalSuggestionState(null);
    } else {
      updateGlobalSuggestions(
        field,
        value,
        event?.target?.name,
        event?.target?.selectionStart
      );
    }
    if (isFormulaEditableColumn(field) && event?.target?.name) {
      setActiveFormulaColumnKey(currentColumnKey => {
        if (hasFormulaMathOperator(value)) return field.key;
        return currentColumnKey === field.key ? null : currentColumnKey;
      });
    }
    const associatedGlobalUnitField = GLOBAL_UNIT_FIELD_BY_COLUMN[field?.key];
    const associatedGlobalUnit = getGlobalUnitForCellValue(
      field,
      value,
      event?.target?.globalSuggestion
    );
    const previousValue = newRow?.[field?.key] ?? rowData?.[field?.key] ?? '';
    const selectionStart = event?.target?.selectionStart;
    const selectionEnd = event?.target?.selectionEnd;

    const itemRow = {
      ...rowData,
      ...newRow,
      quantity: normalizeRequiredDecimalValue(newRow.quantity, '1'),
      multiplier: normalizeRequiredDecimalValue(newRow.multiplier, '1'),
      [field?.key]: value,
      ...(associatedGlobalUnitField && associatedGlobalUnit
        ? { [associatedGlobalUnitField]: associatedGlobalUnit }
        : {}),
    };

    if (isFormulaInput) {
      const evaluatedValue = evaluateFormulaDisplayValue(value, budgetGlobals);
      itemRow[expressionFieldFor(field.key)] =
        normalizeFormulaPayloadValue(value);
      itemRow.item_total =
        evaluatedValue === null
          ? newRow?.item_total ?? rowData?.item_total ?? 0
          : calculateItemTotal(
              {
                ...itemRow,
                [field.key]: evaluatedValue,
              },
              selectedBudget
            );
    } else {
      if (isFormulaEditableColumn(field)) {
        itemRow[expressionFieldFor(field.key)] = '';
      }
      itemRow.item_total = calculateItemTotal(itemRow, selectedBudget);
    }
    itemRow.column_total =
      parseFloat(itemRow.item_total || 0) +
      parseFloat(itemRow.fringe_total || 0);

    if (!isFormulaInput && field?.pluralize && itemRow[field?.pluralize?.key]) {
      if ('quantity' === field.key) {
        if (itemRow.quantity <= 1) {
          itemRow.unit = itemRow.unit.replace(/s$/, '');
        } else {
          itemRow.unit = pluralize(itemRow.unit, itemRow.quantity);
        }
      }

      if ('multiplier' === field.key) {
        if (itemRow.multiplier <= 1) {
          itemRow.multiplier_unit = itemRow.multiplier_unit?.replace(/s$/, '');
        } else {
          itemRow.multiplier_unit = pluralize(
            itemRow.multiplier_unit,
            itemRow.multiplier
          );
        }
      }
    }

    setNewRow(itemRow);
    setClipboardCellFocus(field, itemRow[field.key]);

    if (
      field?.dataType === 'decimal' &&
      (isIncompleteDecimalValue(value, field) ||
        (isFormulaInput &&
          (isIncompleteFormulaExpressionValue(value) ||
            hasUnresolvedFormulaGlobals(value))))
    ) {
      return;
    }

    multiCellSelection?.applyValueChange?.({
      sourceRowKey: getStableRowKey(itemRow),
      column: field,
      value: value,
      previousValue,
      selectionStart,
      selectionEnd,
    });

    const dataToAdd = processDataToAdd(itemRow);
    const hasAccountNumberPayload = Object.hasOwn(dataToAdd, 'account_number')
      ? (dataToAdd.account_number || '') !== (rowData?.account_number || '')
      : false;

    const hasDataToPost =
      hasAccountNumberPayload ||
      columns?.some(col => {
        const hasColumnValue = Object.hasOwn(dataToAdd, col.key);
        const hasExpressionValue =
          isFormulaEditableColumn(col) &&
          Object.hasOwn(dataToAdd, expressionFieldFor(col.key));
        if (!hasColumnValue && !hasExpressionValue) return false;

        const valueKey = hasColumnValue ? col.key : expressionFieldFor(col.key);
        const stringValue = (dataToAdd[valueKey] ?? '').toString();
        const defaultValue =
          col.defaultValue ?? (col.dataType === 'decimal' ? '0.0000' : '');

        return stringValue !== defaultValue;
      });

    if (!hasDataToPost) return;

    if (addNewRow && (isPlaceholderRow(newRow) || isPlaceholderRow(rowData))) {
      addEmptyRow(newRow?.row_id ?? newRow?.id);
      setAddNewRow(false);
    }

    if (newRow?.id || newRow?.row_id) {
      // Existing server row or local draft row — send through addRow which has
      // its own debounce + coalescing queue in the pipeline.
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      pendingDataRef.current = null;
      addRow(dataToAdd);
    } else {
      // Row without a server id — store pending data for blur save
      pendingDataRef.current = dataToAdd;
    }
  };

  const handleItemTextChange = field => event => {
    dismissAiItemSuggestions();
    handleNewRowChange(field)(event);
  };

  const requestAiItemSuggestions = () => {
    const currentValue = String(newRow?.item ?? rowData?.item ?? '').trim();
    const requestId = ++aiItemRequestIdRef.current;
    if (aiItemTimerRef.current) clearTimeout(aiItemTimerRef.current);
    aiItemAbortControllerRef.current?.abort();
    aiItemAbortControllerRef.current = null;

    if (currentValue || !selectedBudget?.budget || !selectedItem?.id) {
      setAiItemSuggestions([]);
      setAiItemSuggestionIndex(0);
      return;
    }

    const reusableSuggestions = getAiAutocompleteSuggestions(
      aiAutocompleteScopeKey,
      ''
    );
    if (reusableSuggestions !== null) {
      setAiItemSuggestions(reusableSuggestions);
      setAiItemSuggestionIndex(0);
      return;
    }

    setAiItemSuggestions([]);
    setAiItemSuggestionIndex(0);

    aiItemTimerRef.current = setTimeout(async () => {
      const delayedReusableSuggestions = getAiAutocompleteSuggestions(
        aiAutocompleteScopeKey,
        ''
      );
      if (delayedReusableSuggestions !== null) {
        if (requestId === aiItemRequestIdRef.current) {
          setAiItemSuggestions(delayedReusableSuggestions);
          setAiItemSuggestionIndex(0);
        }
        return;
      }

      const controller = new AbortController();
      aiItemAbortControllerRef.current = controller;
      try {
        const response = await API_ENDPOINTS.ai_suggestions.contextual(
          {
            budget_id: selectedBudget.budget,
            category_id: selectedItem.id,
            context_mode: 'detail',
            previous_items: aiItemPreviousItems,
          },
          {
            signal: controller.signal,
          }
        );
        const suggestions = response?.data?.suggestions || [];
        storeAiAutocompleteSuggestions(aiAutocompleteScopeKey, '', suggestions);
        if (requestId !== aiItemRequestIdRef.current) return;

        setAiItemSuggestions(suggestions);
        setAiItemSuggestionIndex(0);
      } catch (error) {
        if (
          requestId !== aiItemRequestIdRef.current ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        setAiItemSuggestions([]);
      } finally {
        if (requestId === aiItemRequestIdRef.current) {
          aiItemAbortControllerRef.current = null;
        }
      }
    }, 150);
  };

  const chooseAiItemSuggestion = field => suggestion => {
    handleNewRowChange(field)(suggestion.label);
    dismissAiItemSuggestions();
  };

  const handleItemSuggestionKeyDown = field => event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      aiItemSuppressKeyUpRef.current = event.key;
      dismissAiItemSuggestions();
      return;
    }
    if (event.key === 'Tab') {
      const hasOpenSuggestions = aiItemSuggestions.length > 0;
      dismissAiItemSuggestions();
      if (hasOpenSuggestions) {
        event.stopPropagation();
      } else {
        onKeyDown(event);
      }
      return;
    }
    if (!aiItemSuggestions.length) {
      onKeyDown(event);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      aiItemSuppressKeyUpRef.current = event.key;
      setAiItemSuggestionIndex(
        current =>
          (current +
            (event.key === 'ArrowDown' ? 1 : -1) +
            aiItemSuggestions.length) %
          aiItemSuggestions.length
      );
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      aiItemSuppressKeyUpRef.current = event.key;
      chooseAiItemSuggestion(field)(aiItemSuggestions[aiItemSuggestionIndex]);
      return;
    }
    onKeyDown(event);
  };

  const handleItemSuggestionKeyUp = event => {
    if (
      aiItemSuppressKeyUpRef.current === event.key ||
      (aiItemSuggestions.length &&
        ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key))
    ) {
      event.preventDefault();
      event.stopPropagation();
      aiItemSuppressKeyUpRef.current = null;
      return;
    }

    onKeyUp(event);
  };

  const handleCellFocus = () => {
    focusCountRef.current++;
    isRowFocusedRef.current = true;
  };

  const handleCellBlur = () => {
    focusCountRef.current--;

    setTimeout(() => {
      // The row lost focus and should push the changes
      if (focusCountRef.current === 0 && pendingDataRef.current) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        addRow(pendingDataRef.current);
        pendingDataRef.current = null;
      }

      if (focusCountRef.current <= 0) {
        focusCountRef.current = 0;
        isRowFocusedRef.current = false;
      }
    }, 10);
  };

  const isEditablePresenceColumn = column => {
    if (!rowData?.id || !canEdit || hiddenColumn[column.key]) return false;
    return (
      column?.type === 'text' ||
      column?.type === 'select' ||
      column?.key === 'fringes' ||
      column?.key === 'tags'
    );
  };

  const isClipboardEditableColumn = column =>
    !hiddenColumn[column.key] &&
    (column?.type === 'text' || column?.type === 'select');

  const getClipboardCellDisplayValue = column => {
    if (!column) return '';
    if (isFormulaEditableColumn(column)) {
      const expression = newRow?.[expressionFieldFor(column.key)];
      if (expression) return expression;
    }
    return newRow?.[column.key] ?? rowData?.[column.key] ?? '';
  };

  const setClipboardCellFocus = (column, valueOverride) => {
    if (!isClipboardEditableColumn(column)) return;

    onClipboardCellFocus({
      rowKey: getStableRowKey(rowData),
      rowId: rowData.id,
      rowIndex: index,
      columnKey: column.key,
      rowLabel: newRow?.item || rowData?.item || '',
      displayValue:
        valueOverride !== undefined
          ? valueOverride
          : getClipboardCellDisplayValue(column),
    });
  };

  const getCellPresence = column => {
    if (!rowData?.id) return null;
    return cellPresenceMap.get(
      `${detailSheetType}:${rowData.id}:${column.key}`
    );
  };

  const getCommentTarget = (column, element) => {
    if (
      !rowData?.id ||
      isPlaceholderRow(rowData) ||
      !column?.key ||
      column.key === 'checkbox' ||
      column.isOvertimeRate
    ) {
      return null;
    }

    const rect = element?.getBoundingClientRect?.();
    return {
      detailSheetType,
      rowId: rowData.id,
      columnKey: column.key,
      rowLabel: newRow?.item || rowData?.item || '',
      anchorRect: rect
        ? {
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right,
            top: rect.top,
          }
        : null,
    };
  };

  const handleCellPresenceFocus = column => event => {
    handleCellFocus();

    const focusedCommentTarget = getCommentTarget(
      column,
      event.currentTarget?.closest?.('td')
    );
    if (focusedCommentTarget) {
      onCommentCellFocus(focusedCommentTarget);
    }

    if (column?.key === 'item' && itemEditStartValueRef.current === null) {
      itemEditStartValueRef.current = newRow?.item ?? rowData?.item ?? '';
    }
    if (
      column?.key === 'item' &&
      itemAutocompleteRef.current?.contains(event.target)
    ) {
      requestAiItemSuggestions();
    }

    setClipboardCellFocus(column);

    if (!isEditablePresenceColumn(column)) return;

    const selectedCells =
      multiCellSelection?.getPresenceCells?.(rowData, column) || [];

    onDetailCellFocus({
      detailSheetType,
      rowId: rowData.id,
      columnKey: column.key,
      rowLabel: newRow?.item || rowData?.item || '',
    });

    onCellPresenceFocus({
      detailSheetType,
      rowId: rowData.id,
      columnKey: column.key,
      rowLabel: newRow?.item || rowData?.item || '',
      selectedCells,
    });
  };

  const handleCellPresenceBlur = column => event => {
    if (event.currentTarget?.contains?.(event.relatedTarget)) return;

    handleCellBlur();
    if (column?.key === 'item') {
      dismissAiItemSuggestions();
      onItemFieldCommit({
        row: { ...rowData, ...newRow },
        previousItem: itemEditStartValueRef.current ?? rowData?.item ?? '',
        nextItem: newRow?.item ?? rowData?.item ?? '',
      });
      itemEditStartValueRef.current = null;
    }
    if (isEditablePresenceColumn(column)) {
      onCellPresenceBlur();
    }
  };

  const handleRateOnFocus = field => event => {
    if (!isFormulaEditableColumn(field)) return;
    const expression = newRow?.[expressionFieldFor(field.key)];
    const value = event?.target?.value || field?.defaultValue;
    const nextValue = expression || fixedDecimalRate(value);
    expressionDisplayBackupRef.current[field.key] =
      newRow?.[field.key] ?? rowData?.[field.key] ?? '';
    const itemRow = {
      ...newRow,
      [field?.key]: nextValue,
    };
    setNewRow(itemRow);
    setActiveFormulaColumnKey(
      hasFormulaMathOperator(nextValue) ? field.key : null
    );
    updateGlobalSuggestions(
      field,
      nextValue,
      event?.target?.name,
      event?.target?.selectionStart
    );
  };

  const handleRateOnBlur = field => event => {
    if (!isFormulaEditableColumn(field)) return;
    if (
      event?.relatedTarget?.closest?.(
        `[data-formula-bar-column="${field.key}"]`
      )
    ) {
      setGlobalSuggestionState(null);
      return;
    }
    const value = event?.target?.value ?? '';
    if (isFormulaExpressionValue(value)) {
      const evaluatedValue = evaluateFormulaDisplayValue(value, budgetGlobals);
      setNewRow(prev => ({
        ...prev,
        [field?.key]:
          evaluatedValue ??
          formatDecimalDisplayValue(
            rowData?.[field?.key] ??
              expressionDisplayBackupRef.current[field.key] ??
              prev?.[field?.key]
          ),
      }));
      hideGlobalSuggestions();
      setActiveFormulaColumnKey(null);
      return;
    }

    setNewRow(prev => ({
      ...prev,
      [field?.key]: formatRateWithCommas(value),
    }));
    hideGlobalSuggestions();
    setActiveFormulaColumnKey(null);
  };

  const finishFormulaBarEditing = field => {
    const value = newRow?.[field.key] ?? '';
    if (isFormulaExpressionValue(value)) {
      const evaluatedValue = evaluateFormulaDisplayValue(value, budgetGlobals);
      setNewRow(prev => ({
        ...prev,
        [field.key]:
          evaluatedValue ??
          formatDecimalDisplayValue(
            rowData?.[field.key] ??
              expressionDisplayBackupRef.current[field.key] ??
              prev?.[field.key]
          ),
      }));
    } else {
      setNewRow(prev => ({
        ...prev,
        [field.key]: formatRateWithCommas(value),
      }));
    }
    setGlobalSuggestionState(null);
    setActiveFormulaColumnKey(null);
  };

  const stopGlobalSuggestionKeyboardEvent = event => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();
  };

  const selectGlobalSuggestion = (field, suggestion, event) => {
    stopGlobalSuggestionKeyboardEvent(event);

    const state = globalSuggestionState;
    if (!state || !suggestion) return;

    const currentValue = String(newRow?.[field.key] ?? '');
    const nextValue = `${currentValue.slice(0, state.tokenStart)}${
      suggestion.abbreviation
    }${currentValue.slice(state.tokenEnd)}`;
    const nextCursor = state.tokenStart + suggestion.abbreviation.length;

    handleNewRowChange(field)({
      target: {
        name: state.inputName || `${field.key},${index}`,
        value: nextValue,
        selectionStart: nextCursor,
        selectionEnd: nextCursor,
        globalSuggestion: suggestion,
      },
    });
    setGlobalSuggestionState(null);

    setTimeout(() => {
      const input = document.getElementsByName(
        state.inputName || `${field.key},${index}`
      )?.[0];
      input?.focus();
      input?.setSelectionRange?.(nextCursor, nextCursor);
    }, 0);
  };

  const handleGlobalSuggestionSelect = (field, suggestion) => event => {
    selectGlobalSuggestion(field, suggestion, event);
  };

  const handleGlobalSuggestionKeyDown = field => event => {
    const state = globalSuggestionState;
    if (state?.columnKey !== field.key || !state?.suggestions?.length) {
      onKeyDown?.(event);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      globalSuggestionHandledKeyRef.current = event.key;
      stopGlobalSuggestionKeyboardEvent(event);

      setGlobalSuggestionState(previousState => {
        if (
          previousState?.columnKey !== field.key ||
          !previousState?.suggestions?.length
        ) {
          return previousState;
        }

        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const suggestionCount = previousState.suggestions.length;
        const currentIndex = previousState.activeIndex ?? 0;

        return {
          ...previousState,
          activeIndex:
            (currentIndex + direction + suggestionCount) % suggestionCount,
        };
      });
      return;
    }

    if (event.key === 'Enter') {
      globalSuggestionHandledKeyRef.current = event.key;
      const suggestion = state.suggestions[state.activeIndex ?? 0];
      selectGlobalSuggestion(field, suggestion, event);
      return;
    }

    if (event.key === 'Escape') {
      globalSuggestionHandledKeyRef.current = event.key;
      stopGlobalSuggestionKeyboardEvent(event);
      setGlobalSuggestionState(null);
      return;
    }

    onKeyDown?.(event);
  };

  const handleGlobalSuggestionKeyUp = field => event => {
    if (
      globalSuggestionHandledKeyRef.current &&
      globalSuggestionHandledKeyRef.current === event.key
    ) {
      globalSuggestionHandledKeyRef.current = null;
      stopGlobalSuggestionKeyboardEvent(event);
      return;
    }

    onKeyUp?.(event);
  };

  const renderGlobalSuggestions = column => {
    if (
      globalSuggestionState?.columnKey !== column.key ||
      !globalSuggestionState?.suggestions?.length
    ) {
      return null;
    }

    return (
      <div
        className="global-formula-suggestions"
        role="listbox"
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {globalSuggestionState.suggestions.map(
          (suggestion, suggestionIndex) => (
            <button
              type="button"
              key={suggestion.id || suggestion.abbreviation}
              className={`global-formula-suggestions__item ${
                suggestionIndex === globalSuggestionState.activeIndex
                  ? 'global-formula-suggestions__item--active'
                  : ''
              }`}
              role="option"
              aria-selected={
                suggestionIndex === globalSuggestionState.activeIndex
              }
              onMouseEnter={() => {
                setGlobalSuggestionState(previousState =>
                  previousState?.columnKey === column.key
                    ? {
                        ...previousState,
                        activeIndex: suggestionIndex,
                      }
                    : previousState
                );
              }}
              onMouseDown={handleGlobalSuggestionSelect(column, suggestion)}
            >
              <span className="global-formula-suggestions__abbr">
                {suggestion.abbreviation}
              </span>
              <span className="global-formula-suggestions__name">
                {suggestion.name || suggestion.abbreviation}
              </span>
              <span className="global-formula-suggestions__value">
                {[suggestion.displayValue, suggestion.units]
                  .filter(Boolean)
                  .join(' ')}
              </span>
            </button>
          )
        )}
      </div>
    );
  };

  useEffect(() => {
    if (newRow.multiplier_unit) {
      setUnitSuggestions([newRow.unit]);
    }
  }, [newRow.multiplier_unit, newRow.unit]);

  useEffect(() => {
    if (!aiItemSuggestions.length || !itemAutocompleteRef.current) {
      setAiItemMenuBounds(null);
      return undefined;
    }

    const cellElement = itemAutocompleteRef.current.closest('td');
    const updateMenuBounds = () => {
      const inputRect = itemAutocompleteRef.current.getBoundingClientRect();
      const cellRect = cellElement?.getBoundingClientRect() || inputRect;
      setAiItemMenuBounds({
        left: cellRect.left,
        top: inputRect.bottom + 2,
        width: cellRect.width,
      });
    };

    updateMenuBounds();
    const resizeObserver =
      typeof ResizeObserver === 'undefined' || !cellElement
        ? null
        : new ResizeObserver(updateMenuBounds);
    resizeObserver?.observe(cellElement);
    window.addEventListener('resize', updateMenuBounds);
    window.addEventListener('scroll', updateMenuBounds, true);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMenuBounds);
      window.removeEventListener('scroll', updateMenuBounds, true);
    };
  }, [aiItemSuggestions.length]);

  useEffect(() => {
    if (!aiItemSuggestions.length) return undefined;

    const dismissSuggestionsOnOutsideClick = event => {
      if (
        itemAutocompleteRef.current?.contains(event.target) ||
        aiItemMenuRef.current?.contains(event.target)
      ) {
        return;
      }
      dismissAiItemSuggestions();
    };

    document.addEventListener('mousedown', dismissSuggestionsOnOutsideClick);
    return () => {
      document.removeEventListener(
        'mousedown',
        dismissSuggestionsOnOutsideClick
      );
    };
  }, [aiItemSuggestions.length, dismissAiItemSuggestions]);
  useEffect(() => {
    setTabActive('');
  }, [setTabActive]);

  // Keep addRowRef pointing at the latest addRow so cleanup never uses a stale closure
  useEffect(() => {
    addRowRef.current = addRow;
  });

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Push any pending updates on unmount
  useEffect(() => {
    return () => {
      if (pendingDataRef.current) {
        clearDebounceTimer();

        addRowRef.current(pendingDataRef.current);
        pendingDataRef.current = null;
      }
      if (globalSuggestionHideTimerRef.current) {
        clearTimeout(globalSuggestionHideTimerRef.current);
      }
      if (aiItemTimerRef.current) clearTimeout(aiItemTimerRef.current);
      aiItemAbortControllerRef.current?.abort();
    };
  }, [clearDebounceTimer]);

  useEffect(() => {
    if (fringeOptions && rowData?.fringe) {
      const total = rowData.fringe.reduce((sum, fringeId) => {
        const fringe = fringeOptions.find(option => option.value === fringeId);
        return sum + (fringe?.value || 0); // Add fringe value or 0 if not found
      }, 0);
      setFringeTotal(total);
    }
  }, [fringeOptions, rowData]);

  const renderAiItemSuggestions = column => {
    if (
      !canEdit ||
      !aiItemSuggestions.length ||
      !aiItemMenuBounds ||
      String(newRow?.item ?? rowData?.item ?? '').trim() ||
      typeof document === 'undefined'
    ) {
      return null;
    }

    return createPortal(
      <div
        className="ai-item-autocomplete__menu ai-item-autocomplete__menu--portal"
        role="listbox"
        ref={aiItemMenuRef}
        style={aiItemMenuBounds}
      >
        {aiItemSuggestions.map((suggestion, suggestionIndex) => (
          <button
            key={`${suggestion.label}-${suggestionIndex}`}
            type="button"
            role="option"
            aria-selected={suggestionIndex === aiItemSuggestionIndex}
            className={
              suggestionIndex === aiItemSuggestionIndex ? 'is-active' : ''
            }
            onMouseDown={event => {
              event.preventDefault();
              chooseAiItemSuggestion(column)(suggestion);
            }}
          >
            {suggestion.label}
          </button>
        ))}
      </div>,
      document.body,
      `ai-item-autocomplete-${rowData?.id || rowData?.row_id || index}`
    );
  };

  const activeFormulaColumn = columns.find(
    column => column.key === activeFormulaColumnKey
  );
  const formulaBarPortalTarget =
    typeof document === 'undefined'
      ? null
      : document.getElementById('detail-formula-bar-portal');
  const formulaBar =
    activeFormulaColumn &&
    formulaBarPortalTarget &&
    hasFormulaMathOperator(newRow?.[activeFormulaColumn.key])
      ? createPortal(
          <FormulaBar
            columnKey={activeFormulaColumn.key}
            inputName={`${activeFormulaColumn.key},${index}`}
            onChange={handleNewRowChange(activeFormulaColumn)}
            onFinish={() => finishFormulaBarEditing(activeFormulaColumn)}
            onKeyDown={handleGlobalSuggestionKeyDown(activeFormulaColumn)}
            onKeyUp={handleGlobalSuggestionKeyUp(activeFormulaColumn)}
            value={newRow?.[activeFormulaColumn.key] ?? ''}
            variables={normalizedBudgetGlobals}
          />,
          formulaBarPortalTarget
        )
      : null;

  return (
    <>
      {formulaBar}
      {columns.map(column => {
        const cellPresence = getCellPresence(column);
        const commentTarget = getCommentTarget(column);
        const commentCount = commentTarget
          ? commentCounts.get(
              `${detailSheetType}:${rowData.id}:${column.key}`
            ) || 0
          : 0;
        const isMultiCellSelected =
          multiCellSelection?.isCellSelected?.(rowData, column) || false;
        const isColumnDragging =
          columnDragState?.draggingColumnKey ===
          (column.columnGroupKey || column.key);
        const hasRowActions = canEdit && column.key === 'item' && !hideOptions;
        const isCommentColumnCompact =
          column.key === 'comment' && column.isCompact;
        const formInputColumnState = isCommentColumnCompact
          ? { ...hiddenColumn, comment: true }
          : hiddenColumn;
        const formInputClassName = isCommentColumnCompact
          ? 'form-input-group-collapsed'
          : 'form-input-group-expanded';
        const cellStyle = {
          ...(hiddenColumn[column.key]
            ? {
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                overflow: column.overflow,
              }
            : column.key === 'checkbox'
            ? {
                width: '40px',
                textAlign: 'center',
                backgroundColor: '#f5f5f5',
                padding: '8px',
              }
            : {
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
              }),
          ...(cellPresence
            ? { '--presence-color': cellPresence.user_color }
            : {}),
        };
        const rowCurrency = getRowCurrency(newRow);
        const columnPrefix = column.isOvertimeRate
          ? null
          : ['rate', 'actual'].includes(column.key)
          ? getCurrencySymbol(rowCurrency)
          : column.prefix;
        const columnCellValue =
          newRow?.[column.key] ??
          rowData?.[column.key] ??
          (column.displayValueKey
            ? newRow?.[column.displayValueKey] ??
              rowData?.[column.displayValueKey] ??
              ''
            : '');
        const showPrimaryCurrencyColumnTotal =
          column.key === 'column_total' &&
          rowCurrency !== primaryCurrency &&
          rowData?.column_total_primary_currency !== null &&
          rowData?.column_total_primary_currency !== undefined;
        const overtimePreviewRow = column.isOvertimeRate
          ? { ...rowData, ...newRow }
          : null;
        const overtimeUnitRate = column.isOvertimeRate
          ? calculateOvertimeUnitRate(
              overtimePreviewRow,
              selectedBudget,
              column.overtimeSlot
            )
          : null;
        const formattedOvertimeUnitRate =
          overtimeUnitRate === null
            ? null
            : formatCurrency(overtimeUnitRate, rowCurrency);
        const overtimeUnitName =
          pluralize.singular(String(overtimePreviewRow?.unit || '').trim()) ||
          'unit';
        const overtimeRateTitle = formattedOvertimeUnitRate
          ? `${formattedOvertimeUnitRate} per ${overtimeUnitName}`
          : 'Enter a base rate to preview the overtime rate';

        return (
          <td
            key={`new-row && ${column?.key}`}
            style={cellStyle}
            data-detail-row-id={rowData?.id || rowData?.row_id || ''}
            data-detail-column-key={column.key}
            data-detail-column-group-key={column.columnGroupKey || column.key}
            data-detail-comment-disabled={column.isOvertimeRate || undefined}
            data-detail-row-draft={isPlaceholderRow(rowData) || undefined}
            onMouseDown={event => {
              if (event.target?.closest?.('.tag-cell-picker')) return;
              const clickedCommentTarget = getCommentTarget(
                column,
                event.currentTarget
              );
              if (event.button === 0 && clickedCommentTarget) {
                onCommentCellFocus(clickedCommentTarget);
              }
              setClipboardCellFocus(column);
              multiCellSelection?.handleCellMouseDown?.({
                event,
                rowData,
                column,
              });
            }}
            onContextMenu={event => {
              if (
                event.target?.closest?.('.dropdown, button, [role="button"]') ||
                multiCellSelection?.selectedCellKeys?.size > 1
              ) {
                event.preventDefault();
                return;
              }
              const contextCommentTarget = getCommentTarget(
                column,
                event.currentTarget
              );
              if (!contextCommentTarget) return;
              event.preventDefault();
              event.stopPropagation();
              onCommentCellFocus(contextCommentTarget);
              onCellCommentContextMenu({
                event,
                target: contextCommentTarget,
              });
            }}
            onMouseEnter={event =>
              multiCellSelection?.handleCellMouseEnter?.({
                event,
                rowData,
                column,
              })
            }
            className={`${column?.key} ${
              cellPresence ? 'cell-presence-active' : ''
            } ${isMultiCellSelected ? 'multi-cell-selected' : ''} ${
              isColumnDragging ? 'is-column-dragging' : ''
            }`}
          >
            <div
              className="col-content"
              onFocus={handleCellPresenceFocus(column)}
              onBlur={handleCellPresenceBlur(column)}
              tabIndex={-1}
            >
              {column.key === 'estimate_to_complete_override' &&
                (newRow?.estimate_to_complete_override ??
                  rowData?.estimate_to_complete_override) == null && (
                  <span
                    className="etc-auto-badge"
                    title="Automatic ETC. Enter a value to override it; clear the cell to restore Auto."
                  >
                    Auto
                  </span>
                )}
              {commentCount > 0 && (
                <button
                  aria-label={`Open ${commentCount} comments`}
                  className={`cell-comment-badge ${
                    hasRowActions ? 'cell-comment-badge--with-row-actions' : ''
                  }`}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    const target = getCommentTarget(
                      column,
                      event.currentTarget.closest('td')
                    );
                    if (!target) return;
                    onCommentCellFocus(target);
                    onOpenCellComments(target, { toggle: true });
                  }}
                  onMouseDown={event => event.stopPropagation()}
                  type="button"
                >
                  {commentCount}
                </button>
              )}
              {columnPrefix && <div className="prefix">{columnPrefix}</div>}
              {column?.type === 'text' && column?.key === 'item' && (
                <div className="ai-item-autocomplete" ref={itemAutocompleteRef}>
                  <FormInput
                    inputClassName={formInputClassName}
                    hiddenColumn={formInputColumnState}
                    placeholder={column.placeholder}
                    maxLength={column.maxLength}
                    onChange={handleItemTextChange(column)}
                    editComment={handleItemTextChange(column)}
                    onKeyUp={handleItemSuggestionKeyUp}
                    onKeyDown={handleItemSuggestionKeyDown(column)}
                    name={column.key + ',' + index}
                    value={newRow?.[column.key] ?? ''}
                    readOnly={!canEdit}
                    isComment={column.key}
                    style={{ background: 'transparent' }}
                    formControlValue={formControlValue}
                    itemType={column?.key}
                    rowData={newRow}
                    rowId={String(newRow?.id || '')}
                  />
                  {renderAiItemSuggestions(column)}
                </div>
              )}
              {column?.type === 'text' && column?.key !== 'item' && (
                <FormInput
                  inputClassName={formInputClassName}
                  hiddenColumn={formInputColumnState}
                  placeholder={column.placeholder}
                  maxLength={column.maxLength}
                  onChange={handleNewRowChange(column)}
                  editComment={handleNewRowChange(column)}
                  onKeyUp={
                    isFormulaEditableColumn(column)
                      ? handleGlobalSuggestionKeyUp(column)
                      : onKeyUp
                  }
                  onKeyDown={
                    isFormulaEditableColumn(column)
                      ? handleGlobalSuggestionKeyDown(column)
                      : onKeyDown
                  }
                  name={column.key + ',' + index}
                  value={columnCellValue}
                  readOnly={!canEdit}
                  isComment={column.key}
                  style={{ background: 'transparent' }}
                  formControlValue={formControlValue}
                  itemType={column?.key}
                  rowData={newRow}
                  onFocus={
                    isFormulaEditableColumn(column)
                      ? handleRateOnFocus(column)
                      : undefined
                  }
                  onBlur={
                    isFormulaEditableColumn(column)
                      ? handleRateOnBlur(column)
                      : undefined
                  }
                  rowId={String(newRow?.id || '')}
                />
              )}
              {column.isOvertimeRate && (
                <output
                  aria-label={`${column.managerLabel} overtime rate: ${overtimeRateTitle}`}
                  className="overtime-rate-cell"
                  title={overtimeRateTitle}
                >
                  {formattedOvertimeUnitRate || '—'}
                </output>
              )}
              {isFormulaEditableColumn(column) &&
                renderGlobalSuggestions(column)}
              {column?.type === 'select' && (
                <TypeAheadInput
                  placeholder={column.placeholder}
                  options={
                    column.key !== 'currency' && unitSuggestions.length > 0
                      ? unitSuggestions
                      : column.options
                  }
                  onChange={handleNewRowChange(column)}
                  onInputEdit={
                    multiCellSelection?.isPrecisionTextEditActive?.(
                      rowData,
                      column
                    )
                      ? handleNewRowChange(column)
                      : undefined
                  }
                  defaultValue={newRow?.[column.key] ?? ''}
                  onKeyUp={onKeyUp}
                  name={column.key + ',' + index}
                  readOnly={!canEdit}
                  strictSelect={column.key === 'currency'}
                />
              )}
              {column?.type === 'tags' && (
                <TagCellPicker
                  options={tagOptions}
                  selectedValues={newRow?.tags || rowData?.tags || []}
                  onChange={handleTagChange}
                  readOnly={!canEdit}
                  onTagsChanged={refetchBudgetTags}
                />
              )}
              {column?.key === 'item_total' && (
                <div className="prefix">
                  {formatCurrency(newRow?.[column.key] || 0, rowCurrency)}
                </div>
              )}
              {hasRowActions && (
                <DropDownMenu
                  menuOptions={lineItemOptions}
                  toggleIcon={<MoreIcon className="more-icon" />}
                  onChange={handleRowAction(
                    appliesToMultipleRows ? selectedMenuRows : newRow
                  )}
                />
              )}
              {canEdit && column?.key === 'fringes' && (
                <MultiSelectDropdown
                  focus={focus}
                  ItemRow={{
                    ...newRow,
                    item_total: parseFloat(newRow?.item_total || 0),
                  }}
                  detailSheetType={detailSheetType}
                  fringeDetailsData={fringeData}
                  refreshLinkedFringes={refreshLinkedFringes}
                  groupFringeData={groupOptions}
                  groupFringeDetails={groupFringeData}
                  toggleIcon={<span className="custom-icon">+</span>}
                  menuOptions={fringeOptions}
                  selectedValues={selectedFruits}
                  onChange={handleFruitChange}
                  fringeRowCount={fringeRowCount}
                  //setShowCheckboxColumn={setShowCheckboxColumn}
                  selectedFringe={selectedFringe}
                  setSelectedFringe={setSelectedFringe}
                  setShowModal={setShowModal}
                  hoverData={hoverData}
                  highlightedRows={highlightedRows}
                  fringeGroups={fringeGroups}
                  rowId={String(rowData.id || '')}
                  hoveredFringeId={hoveredFringeId}
                  setHoveredFringeId={setHoveredFringeId}
                  hoveredRowId={hoveredRowId}
                  setHoveredRowId={setHoveredRowId}
                />
              )}
              {column?.key === 'fringe_total' && (
                <div className="prefix">
                  {rowData?.fringe_total
                    ? formatCurrency(rowData.fringe_total, rowCurrency)
                    : formatCurrency(0, rowCurrency)}
                </div>
              )}
              {column?.key === 'column_total' && (
                <div
                  className={`prefix ${
                    showPrimaryCurrencyColumnTotal ? 'prefix--stacked' : ''
                  }`}
                >
                  <span>
                    {formatCurrency(
                      parseFloat(newRow?.item_total || 0) +
                        parseFloat(
                          rowData?.fringe_total ?? newRow?.fringe_total ?? 0
                        ),
                      rowCurrency
                    )}
                  </span>
                  {showPrimaryCurrencyColumnTotal && (
                    <span className="primary-currency-total">
                      {formatCurrency(
                        rowData.column_total_primary_currency,
                        primaryCurrency
                      )}
                    </span>
                  )}
                </div>
              )}
              {cellPresence && (
                <span
                  className="cell-presence-badge"
                  title={`${cellPresence.user_name} is editing this cell`}
                >
                  {cellPresence.user_initials}
                </span>
              )}
            </div>
          </td>
        );
      })}
    </>
  );
};

ItemRow.propTypes = {
  columns: PropTypes.array.isRequired,
  rowData: PropTypes.object,
  allRows: PropTypes.array,
  addRow: PropTypes.func.isRequired,
  onItemFieldCommit: PropTypes.func,
  hiddenColumn: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  handleRowAction: PropTypes.func.isRequired,
  addEmptyRow: PropTypes.func.isRequired,
  hideOptions: PropTypes.bool,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  index: PropTypes.number.isRequired,
  lastIndex: PropTypes.number.isRequired,
  lastObjectId: PropTypes.number.isRequired,
  focusEl: PropTypes.node,
  setFocusEl: PropTypes.func,
  focus: PropTypes.bool,
  fringeRowCount: PropTypes.object,
  isChecked: PropTypes.bool,
  onCheckboxChange: PropTypes.func,
  setShowCheckboxColumn: PropTypes.func,
  setSelectedFringe: PropTypes.func,
  fringeRows: PropTypes.object,
  selectedFringe: PropTypes.object,
  setShowModal: PropTypes.func,
  hoverData: PropTypes.object,
  highlightedRows: PropTypes.array,
  fringeGroups: PropTypes.object,
  hoveredFringeId: PropTypes.string,
  setHoveredFringeId: PropTypes.func,
  hoveredRowId: PropTypes.string,
  setHoveredRowId: PropTypes.func,
  refreshLinkedFringes: PropTypes.func.isRequired,
  detailSheetType: PropTypes.string,
  cellPresenceMap: PropTypes.instanceOf(Map),
  onCellPresenceFocus: PropTypes.func,
  onCellPresenceBlur: PropTypes.func,
  onDetailCellFocus: PropTypes.func,
  onClipboardCellFocus: PropTypes.func,
  multiCellSelection: PropTypes.object,
  budgetGlobals: PropTypes.array,
  budgetTags: PropTypes.array,
  refetchBudgetTags: PropTypes.func,
  columnDragState: PropTypes.shape({
    draggingColumnKey: PropTypes.string,
  }),
  selectedItem: PropTypes.object,
  commentCounts: PropTypes.instanceOf(Map),
  onCommentCellFocus: PropTypes.func,
  onCellCommentContextMenu: PropTypes.func,
  onOpenCellComments: PropTypes.func,
};

ItemRow.defaultProps = {
  rowData: {},
  allRows: [],
  onItemFieldCommit: () => null,
  hideOptions: false,
  onKeyUp: e => e,
  onKeyDown: e => e,
  focusEl: null,
  setFocusEl: () => null,
  focus: false,
  fringeRowCount: {},
  isChecked: false,
  onCheckboxChange: () => null,
  setShowCheckboxColumn: () => null,
  setSelectedFringe: () => null,
  fringeRows: {},
  selectedFringe: null,
  setShowModal: () => null,
  hoverData: {},
  highlightedRows: [],
  fringeGroups: {},
  hoveredFringeId: null,
  setHoveredFringeId: () => null,
  hoveredRowId: null,
  setHoveredRowId: () => null,
  detailSheetType: 'swimlane',
  cellPresenceMap: new Map(),
  onCellPresenceFocus: () => null,
  onCellPresenceBlur: () => null,
  onDetailCellFocus: () => null,
  onClipboardCellFocus: () => null,
  multiCellSelection: {},
  budgetGlobals: [],
  budgetTags: [],
  refetchBudgetTags: () => null,
  columnDragState: null,
  selectedItem: null,
  commentCounts: new Map(),
  onCommentCellFocus: () => null,
  onCellCommentContextMenu: () => null,
  onOpenCellComments: () => null,
};
export default React.memo(ItemRow);
